import React from 'react';
import { Users, GraduationCap, Heart, School } from 'lucide-react';
import { Avatar } from '../../shared/Avatar';
import { MasteryBar } from '../../shared/MasteryBar';
import { Badge } from '../../../../components/ui/Badge';
import { timeAgo } from '../../shared/utils';
import { ROLE_CONFIG } from './types';
import type { UserRow, RoleFilter } from './types';
export const learnerColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string, row: UserRow) => <div className="flex items-center gap-2.5" data-id="element-3622">
        <Avatar name={v} data-id="element-3623" />
        <div data-id="element-3624">
          <div className="text-sm font-medium text-gray-900" data-id="element-3625">{v}</div>
          <div className="text-xs text-gray-400" data-id="element-3626">
            {row.grade ? `Groep ${row.grade}` : '—'}
          </div>
        </div>
      </div>
}, {
  key: 'organization',
  label: 'School',
  render: (v: string) => <span className="text-sm text-gray-600" data-id="element-3627">{v}</span>
}, {
  key: 'sessionCount',
  label: 'Sessies',
  render: (v: number) => <span className="text-sm font-medium text-gray-700 tabular-nums" data-id="element-3628">
        {v}
      </span>
}, {
  key: 'avgMastery',
  label: 'Beheersing',
  render: (v: number) => <MasteryBar value={v || 0} data-id="element-3629" />
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-3630">{timeAgo(v)}</span>
}];
export const teacherColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string, row: UserRow) => <div className="flex items-center gap-2.5" data-id="element-3631">
        <Avatar name={v} data-id="element-3632" />
        <div data-id="element-3633">
          <div className="text-sm font-medium text-gray-900" data-id="element-3634">{v}</div>
          <div className="text-xs text-gray-400" data-id="element-3635">
            {row.groupName ?? (row.account && 'grade' in row.account ? `Groep ${row.account.grade}` : '—')}
          </div>
        </div>
      </div>
}, {
  key: 'organization',
  label: 'School',
  render: (v: string) => <span className="text-sm text-gray-600" data-id="element-3636">{v}</span>
}, {
  key: 'linkedCount',
  label: 'Leerlingen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-3637">
        <Users className="w-3.5 h-3.5 text-gray-400" data-id="element-3638" />
        {v}
      </span>
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-3639">{timeAgo(v)}</span>
}];
export const parentColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-3640">
        <Avatar name={v} data-id="element-3641" />
        <span className="text-sm font-medium text-gray-900" data-id="element-3642">{v}</span>
      </div>
}, {
  key: 'linkedCount',
  label: 'Kinderen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-3643">
        <Heart className="w-3.5 h-3.5 text-rose-400" data-id="element-3644" />
        {v}
      </span>
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-3645">{timeAgo(v)}</span>
}];
export const allColumns = [{
  key: 'name',
  label: 'Gebruiker',
  render: (_value: unknown, row: UserRow) => <div className="flex items-center gap-3" data-id="element-3646">
        <Avatar name={row.name} data-id="element-3647" />
        <div data-id="element-3648">
          <div className="font-medium text-gray-900 text-sm" data-id="element-3649">{row.name}</div>
          <div className="text-xs text-gray-400" data-id="element-3650">{row.email}</div>
        </div>
      </div>
}, {
  key: 'role',
  label: 'Rol',
  render: (_value: unknown, row: UserRow) => <Badge variant={ROLE_CONFIG[row.role].variant} size="sm" data-id="element-3651">
        {ROLE_CONFIG[row.role].label}
      </Badge>
}, {
  key: 'organization',
  label: 'Organisatie',
  render: (v: string) => <span className="text-sm text-gray-600 truncate max-w-[140px] block" data-id="element-3652">
        {v}
      </span>
}, {
  key: 'linkedCount',
  label: 'Koppelingen',
  render: (v: number) => <span className="text-sm text-gray-700" data-id="element-3653">{v > 0 ? v : '—'}</span>
}, {
  key: 'xp',
  label: 'XP',
  render: (v: number, row: UserRow) => row.role === 'learner' ? <span className="text-sm font-bold text-violet-600" data-id="element-3654">
          {v.toLocaleString('nl-NL')}
        </span> : <span className="text-xs text-gray-400" data-id="element-3655">—</span>
}, {
  key: 'lastActiveFormatted',
  label: 'Actief',
  render: (v: string) => <span className="text-xs text-gray-500" data-id="element-3656">{v}</span>
}];
export const schoolColumns = [{
  key: 'name',
  label: 'School',
  render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-3657">
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-3658">
          <School className="w-4 h-4 text-purple-600" data-id="element-3659" />
        </div>
        <span className="text-sm font-medium text-gray-900" data-id="element-3660">{v}</span>
      </div>
}, {
  key: 'learnerCount',
  label: 'Leerlingen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-3661">
        <Users className="w-3.5 h-3.5 text-gray-400" data-id="element-3662" />
        {v}
      </span>
}, {
  key: 'teacherCount',
  label: 'Docenten',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-3663">
        <GraduationCap className="w-3.5 h-3.5 text-gray-400" data-id="element-3664" />
        {v}
      </span>
}, {
  key: 'avgMastery',
  label: 'Gem. beheersing',
  render: (v: number) => <MasteryBar value={v} data-id="element-3665" />
}];
export function getColumnsForRole(roleFilter: RoleFilter) {
  switch (roleFilter) {
    case 'learner':
      return learnerColumns;
    case 'teacher':
      return teacherColumns;
    case 'parent':
      return parentColumns;
    case 'school':
      return schoolColumns;
    default:
      return allColumns;
  }
}